const WISHLIST_SESSION_KEY = "shopify-wishlist";
const WISHLIST_LEGACY_LOCAL_KEY = "shopify-wishlist";
const WISHLIST_DELIMITER = ",";

function migrateWishlistFromLocalStorageOnce() {
  try {
    if (sessionStorage.getItem(WISHLIST_SESSION_KEY)) return;
    const legacy = localStorage.getItem(WISHLIST_LEGACY_LOCAL_KEY);
    if (legacy) {
      sessionStorage.setItem(WISHLIST_SESSION_KEY, legacy);
      localStorage.removeItem(WISHLIST_LEGACY_LOCAL_KEY);
    }
  } catch {}
}

function getSessionWishlistHandles() {
  try {
    const raw = sessionStorage.getItem(WISHLIST_SESSION_KEY);
    if (!raw) return [];
    return raw.split(WISHLIST_DELIMITER).filter(Boolean);
  } catch {
    return [];
  }
}

function setSessionWishlistHandles(handles) {
  const list = handles.filter(Boolean);
  try {
    if (list.length)
      sessionStorage.setItem(WISHLIST_SESSION_KEY, list.join(WISHLIST_DELIMITER));
    else sessionStorage.removeItem(WISHLIST_SESSION_KEY);
  } catch {}
}

function getWishlistRootPrefix() {
  const grid = document.querySelector("[grid-wishlist]");
  if (grid?.dataset.wishlistRoot) return grid.dataset.wishlistRoot;
  if (document.body?.dataset.wishlistRoot) return document.body.dataset.wishlistRoot;
  return "/";
}

function parseCustomerWishlistGids() {
  const el = document.getElementById("shopify-customer-wishlist-metafield");
  if (!el) return [];
  const raw = el.textContent.trim();
  if (!raw || raw === "null") return [];
  try {
    let v = JSON.parse(raw);
    if (typeof v === "string") {
      try {
        v = JSON.parse(v);
      } catch {
        return [];
      }
    }
    return Array.isArray(v) ? v.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function normalizeProductGid(entry) {
  if (typeof entry !== "string") return "";
  const s = entry.trim();
  if (!s) return "";
  if (s.startsWith("gid://")) {
    const parts = s.split("/");
    return parts[parts.length - 1] || "";
  }
  if (/^\d+$/.test(s)) return s;
  return "";
}

const WISHLIST_ID_HANDLE_CACHE_KEY = "shopify-wishlist-id-handle-map";

function loadIdHandleCache() {
  try {
    return JSON.parse(sessionStorage.getItem(WISHLIST_ID_HANDLE_CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveIdHandleCache(cache) {
  try {
    const keys = Object.keys(cache);
    if (keys.length > 200) {
      const next = {};
      keys.slice(-200).forEach((k) => {
        next[k] = cache[k];
      });
      cache = next;
    }
    sessionStorage.setItem(WISHLIST_ID_HANDLE_CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

async function resolveGidsToHandles(gids, rootPrefix) {
  const orderedIds = gids.map(normalizeProductGid).filter(Boolean);
  const idSet = new Set(orderedIds.map(String));
  const idToHandle = new Map();
  const limit = 250;
  const maxPages = 40;
  const parallelPages = 8;
  const base = (rootPrefix || "/").replace(/\/$/, "") || "";

  const cache = loadIdHandleCache();
  for (const id of orderedIds) {
    const sid = String(id);
    if (cache[sid]) idToHandle.set(sid, cache[sid]);
  }

  let needed = new Set([...idSet].filter((id) => !idToHandle.has(id)));
  if (needed.size === 0) {
    return orderedIds.map((id) => idToHandle.get(String(id))).filter(Boolean);
  }

  let startPage = 1;
  while (needed.size > 0 && startPage <= maxPages) {
    const endPage = Math.min(startPage + parallelPages - 1, maxPages);
    const pageNums = [];
    for (let p = startPage; p <= endPage; p++) pageNums.push(p);

    const results = await Promise.all(
      pageNums.map((p) =>
        fetch(
          `${base}/collections/all/products.json?limit=${limit}&page=${p}`
        ).then((r) => (r.ok ? r.json() : { products: [] }))
      )
    );

    let anyProducts = false;
    for (const data of results) {
      const products = data.products || [];
      if (products.length) anyProducts = true;
      for (const p of products) {
        const idStr = String(p.id);
        if (needed.has(idStr)) {
          idToHandle.set(idStr, p.handle);
          needed.delete(idStr);
          cache[idStr] = p.handle;
        }
      }
    }

    if (!anyProducts) break;
    startPage = endPage + 1;
  }

  saveIdHandleCache(cache);
  return orderedIds.map((id) => idToHandle.get(String(id))).filter(Boolean);
}

function isProductHandleWishlisted(handle) {
  if (!handle) return false;
  if (getSessionWishlistHandles().includes(handle)) return true;
  const r = window.__shopifyWishlistMetafieldHandles;
  return Array.isArray(r) && r.includes(handle);
}

function refreshAllWishlistHeartStates() {
  document.querySelectorAll("wishlist-item").forEach(function (el) {
    const btn = el.querySelector("button[wishlist-button][data-product-handle]");
    if (!btn) return;
    const h = btn.dataset.productHandle;
    if (!h) return;
    btn.classList.toggle("active", isProductHandleWishlisted(h));
  });
}

function wishlistMetafieldSyncEnabled() {
  const cfg = window.__wishlistMetafieldSync;
  if (!cfg || !cfg.customerLoggedIn) return false;
  const u = String(cfg.url || "").trim();
  if (!u) return false;
  const sentinel = u.toLowerCase();
  if (
    sentinel === "disabled" ||
    sentinel === "none" ||
    sentinel === "off"
  ) {
    return false;
  }
  return true;
}

async function syncWishlistMetafieldWithServer(action, handle) {
  const cfg = window.__wishlistMetafieldSync;
  if (!wishlistMetafieldSyncEnabled()) return true;
  const base = (getWishlistRootPrefix() || "/").replace(/\/$/, "") || "";
  let productJson;
  try {
    const r = await fetch(
      `${base}/products/${encodeURIComponent(handle)}.js`
    );
    if (!r.ok) throw new Error("product_json");
    productJson = await r.json();
  } catch (e) {
    console.error("[Wishlist] sync: could not load product", e);
    return false;
  }
  const productGid = `gid://shopify/Product/${productJson.id}`;
  const syncPath = String(cfg.url).trim();
  const syncUrl = syncPath.startsWith("http")
    ? syncPath
    : `${window.location.origin}${syncPath.startsWith("/") ? "" : "/"}${syncPath}`;
  try {
    const r = await fetch(syncUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, product_gid: productGid, customer_id: cfg.customerId }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data.ok) throw new Error(data.error || String(r.status));
    if (Array.isArray(data.wishlist)) {
      const el = document.getElementById(
        "shopify-customer-wishlist-metafield"
      );
      if (el) el.textContent = JSON.stringify(data.wishlist);
      window.__shopifyWishlistMetafieldGidCount = data.wishlist.length;
      let handles = window.__shopifyWishlistMetafieldHandles;
      if (!Array.isArray(handles)) handles = [];
      if (action === "add" && !handles.includes(handle)) {
        handles = [...handles, handle];
      } else if (action === "remove") {
        handles = handles.filter((h) => h !== handle);
      }
      window.__shopifyWishlistMetafieldHandles = handles;
    }
    refreshAllWishlistCounts();
    refreshAllWishlistHeartStates();
    return true;
  } catch (e) {
    console.error("[Wishlist] metafield sync failed", e);
    return false;
  }
}

function refreshAllWishlistCounts() {
  const local = getSessionWishlistHandles();
  const remoteResolved = window.__shopifyWishlistMetafieldHandles;
  const gidCount = window.__shopifyWishlistMetafieldGidCount || 0;

  let n;
  if (Array.isArray(remoteResolved)) {
    const seen = new Set(remoteResolved);
    n = remoteResolved.length;
    for (const h of local) {
      if (!seen.has(h)) {
        seen.add(h);
        n++;
      }
    }
  } else if (gidCount > 0) {
    // When metafield sync is enabled, local session items are just a client cache of actions
    // that are also written to the customer metafield. If we add local.length here, we can
    // double-count the same item before GIDs are hydrated into handles, causing count flicker.
    //
    // So in sync mode we trust the remote GID count; in non-sync mode we keep the legacy behavior.
    n = wishlistMetafieldSyncEnabled() ? gidCount : gidCount + local.length;
  } else {
    n = local.length;
  }

  document.querySelectorAll(".wishlist__count").forEach((el) => {
    el.textContent = String(n);
  });
}

class WishlistProduct extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.onAddWishlist);
    this.BUTTON_ACTIVE_CLASS = "active";
    this.syncHeartButtonState();
    this.WishlistItemCounter();
  }

  connectedCallback() {
    this.syncHeartButtonState();
  }

  WishlistItemCounter() {
    refreshAllWishlistCounts();
  }

  syncHeartButtonState() {
    const wishlistAddedButton = this.querySelector("button[wishlist-button]");
    if (wishlistAddedButton != null) {
      let productHandle = wishlistAddedButton.dataset.productHandle || false;
      if (!productHandle)
        return console.error(
          "[Wishlist] Missing `data-product-handle` attribute. Failed to update the wishlist."
        );
      wishlistAddedButton.classList.toggle(
        this.BUTTON_ACTIVE_CLASS,
        isProductHandleWishlisted(productHandle)
      );
    }
  }

  onAddWishlist() {
    const wishlistItem = this.querySelector("button");
    let productHandle = wishlistItem.dataset.productHandle || false;
    if (!productHandle)
      return console.error(
        "[Wishlist] Missing `data-product-handle` attribute. Failed to update the wishlist."
      );

    const wasInSession = getSessionWishlistHandles().includes(productHandle);
    const wasInRemote =
      Array.isArray(window.__shopifyWishlistMetafieldHandles) &&
      window.__shopifyWishlistMetafieldHandles.includes(productHandle);
    const wasInWishlist = wasInSession || wasInRemote;

    // Snapshot remote state so we can optimistically update counts/handles and revert on failure.
    const prevRemoteHandles = Array.isArray(window.__shopifyWishlistMetafieldHandles)
      ? [...window.__shopifyWishlistMetafieldHandles]
      : window.__shopifyWishlistMetafieldHandles;
    const prevRemoteGidCount = window.__shopifyWishlistMetafieldGidCount;

    if (wasInWishlist) {
      if (wasInSession) this.updateWishlist(productHandle);
    } else {
      this.updateWishlist(productHandle);
    }

    wishlistItem.classList.toggle(this.BUTTON_ACTIVE_CLASS, !wasInWishlist);

    const action = wasInWishlist ? "remove" : "add";
    const grid = document.querySelector("[grid-wishlist]") || false;
    const rowEl = this.closest(".product-grid-item");
    const self = this;

    // Optimistically update the in-memory remote representation so header counts update immediately
    // even when removing an item that only exists in the customer metafield.
    (function optimisticRemoteUpdate() {
      // If we have resolved handles, mutate them.
      if (Array.isArray(window.__shopifyWishlistMetafieldHandles)) {
        if (action === "add") {
          if (!window.__shopifyWishlistMetafieldHandles.includes(productHandle)) {
            window.__shopifyWishlistMetafieldHandles = [
              ...window.__shopifyWishlistMetafieldHandles,
              productHandle,
            ];
          }
        } else {
          window.__shopifyWishlistMetafieldHandles =
            window.__shopifyWishlistMetafieldHandles.filter((h) => h !== productHandle);
        }
      }

      // If we only have a GID count (or want immediate count changes), adjust it as well.
      // This is best-effort; the server response will reconcile the exact array.
      const n = Number(window.__shopifyWishlistMetafieldGidCount || 0);
      if (action === "add") window.__shopifyWishlistMetafieldGidCount = n + 1;
      else window.__shopifyWishlistMetafieldGidCount = Math.max(0, n - 1);
    })();

    refreshAllWishlistCounts();

    const revertToggle = function () {
      // Revert optimistic remote updates
      window.__shopifyWishlistMetafieldHandles = prevRemoteHandles;
      window.__shopifyWishlistMetafieldGidCount = prevRemoteGidCount;

      if (!wasInWishlist) {
        self.updateWishlist(productHandle);
      } else if (wasInSession) {
        self.updateWishlist(productHandle);
      }
      wishlistItem.classList.toggle(
        self.BUTTON_ACTIVE_CLASS,
        wasInWishlist
      );
      refreshAllWishlistCounts();
      refreshAllWishlistHeartStates();
    };

    const removeGridRow = function () {
      if (!grid || !wasInWishlist || !rowEl) return;
      // Wishlist product cards are wrapped in a Bootstrap column (<div class="col">).
      // Remove the column wrapper to avoid leaving an empty space in the grid.
      const col = rowEl.closest(".col");
      (col || rowEl).remove();
      if (!grid.querySelector(".product-grid-item")) {
        grid.closest(".wishlist-page")?.classList.remove("wishlist_exists");
      }
    };

    if (wishlistMetafieldSyncEnabled()) {
      syncWishlistMetafieldWithServer(action, productHandle).then(function (ok) {
        if (!ok) {
          revertToggle();
          return;
        }
        removeGridRow();
      });
    } else {
      removeGridRow();
    }

    if (!grid) {
      wishlistItem.classList.add("loading", "adding");
      setTimeout(function () {
        wishlistItem.classList.remove("loading");
      }, 500);

      setTimeout(function () {
        document.querySelector(".adding")?.classList.remove("adding");
      }, 1000);
    }

    if (this.getWishlist().length <= 0 && grid && !wishlistMetafieldSyncEnabled()) {
      grid.closest(".wishlist-page")?.classList.remove("wishlist_exists");
    }
  }

  getWishlist() {
    return getSessionWishlistHandles();
  }

  setWishlist(array) {
    setSessionWishlistHandles(array);
    return array.join(WISHLIST_DELIMITER);
  }

  updateWishlist(handle) {
    let wishlist = [...this.getWishlist()];
    let indexInWishlist = wishlist.indexOf(handle);
    if (indexInWishlist === -1) wishlist.push(handle);
    else wishlist.splice(indexInWishlist, 1);
    return this.setWishlist(wishlist);
  }

  wishlistContains(handle) {
    return this.getWishlist().indexOf(handle) !== -1;
  }
}

window.__shopifyWishlistMetafieldGidCount = parseCustomerWishlistGids().length;

customElements.define("wishlist-item", WishlistProduct);

let newWishlistObj = new WishlistProduct();

async function hydrateMetafieldHandles() {
  const gidList = parseCustomerWishlistGids();
  window.__shopifyWishlistMetafieldGidCount = gidList.length;
  if (!gidList.length) {
    window.__shopifyWishlistMetafieldHandles = [];
    refreshAllWishlistCounts();
    refreshAllWishlistHeartStates();
    return [];
  }
  delete window.__shopifyWishlistMetafieldHandles;
  refreshAllWishlistCounts();
  const root = getWishlistRootPrefix();
  const handles = await resolveGidsToHandles(gidList, root);
  window.__shopifyWishlistMetafieldHandles = handles;
  refreshAllWishlistCounts();
  refreshAllWishlistHeartStates();
  return handles;
}

document.addEventListener("DOMContentLoaded", function () {
  migrateWishlistFromLocalStorageOnce();
  refreshAllWishlistHeartStates();
  const grid = document.querySelector("[grid-wishlist]") || false;

  if (grid) {
    setupGrid(grid);
  } else {
    hydrateMetafieldHandles().then(function () {
      // In sync mode, session storage is only a client-side cache. Reconcile it to the remote
      // wishlist so cross-device removals/adds are reflected after a refresh.
      if (wishlistMetafieldSyncEnabled()) {
        try {
          setSessionWishlistHandles([]);
        } catch {}
      }
      refreshAllWishlistHeartStates();
    });
  }
});

document.addEventListener("shopify:section:load", function () {
  refreshAllWishlistHeartStates();
});

var setupGrid = async function (grid) {
  const rootPrefix = grid.dataset.wishlistRoot || getWishlistRootPrefix();

  var wishlistPage = document.querySelector(".wishlist-page");
  wishlistPage?.classList.add("wishlist_loading");

  await hydrateMetafieldHandles();
  refreshAllWishlistHeartStates();

  const fromMetafield = window.__shopifyWishlistMetafieldHandles || [];
  // When sync is enabled, the customer metafield is the source of truth across devices.
  // Do NOT merge in local session handles (they can re-add items removed on another device).
  const merged = wishlistMetafieldSyncEnabled()
    ? [...fromMetafield]
    : (function () {
        const local = getSessionWishlistHandles();
        const seen = new Set(fromMetafield);
        const m = [...fromMetafield];
        for (const h of local) {
          if (!seen.has(h)) {
            seen.add(h);
            m.push(h);
          }
        }
        return m;
      })();

  if (merged.length > 0) {
    wishlistPage?.classList.add("wishlist_exists");
  } else {
    wishlistPage?.classList.remove("wishlist_exists");
  }
  wishlistPage?.classList.remove("wishlist_loading");

  const base = (rootPrefix || "/").replace(/\/$/, "") || "";

  var requests = merged.map(function (handle) {
    var productTileTemplateUrl =
      base + "/products/" + encodeURIComponent(handle) + "?view=wishlist";
    return fetch(productTileTemplateUrl).then(function (res) {
      if (res.status == 200) {
        return res.text();
      }
      return "";
    });
  });

  Promise.all(requests).then(function (responses) {
    grid.innerHTML = responses.join("");
    refreshAllWishlistCounts();
    refreshAllWishlistHeartStates();
  });
};
